import React from 'react';
import { Repeat, Radio, CalendarDays, BarChart3 } from 'lucide-react';
import { MELBOURNE_TZ } from '../../../utils/melbourneTime';

/** Repeat buyer behaviour: the strongest intent signals we hold. */
export function RepeatBuyers({ repeat = {}, onBuyerClick, buyersByKey = {} }) {
    const groups = [
        { key: 'viewed_2_plus', label: 'Viewed 2+ times' },
        { key: 'viewed_5_plus', label: 'Viewed 5+ times' },
        { key: 'viewed_10_plus', label: 'Viewed 10+ times' },
        { key: 'back_in_24h', label: 'Back in the last 24 hours' },
        { key: 'document_downloaders', label: 'Downloaded the documents' },
        { key: 'viewed_after_notification', label: 'Viewed right after we emailed them' },
    ];

    return (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {groups.map((group) => {
                const bucket = repeat[group.key] || { count: 0, buyers: [] };
                const rows = bucket.buyers || [];
                return (
                    <div key={group.key} className="rounded-xl border border-gray-200 bg-white p-3">
                        <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500">
                                <Repeat size={12} className="text-[#55C5D0]" /> {group.label}
                            </span>
                            <span className="text-lg font-extrabold text-[#374151]">{bucket.count}</span>
                        </div>
                        <div className="mt-2 space-y-1">
                            {rows.slice(0, 5).map((row) => (
                                <button
                                    key={row.key}
                                    type="button"
                                    onClick={() => onBuyerClick && buyersByKey[row.key] && onBuyerClick(buyersByKey[row.key])}
                                    className="flex w-full items-center justify-between rounded-lg px-2 py-1 text-left text-xs hover:bg-[#f0fafb]"
                                >
                                    <span className="truncate font-medium text-[#374151]">{row.name}</span>
                                    <span className="ml-2 shrink-0 font-bold text-gray-500">{row.views}</span>
                                </button>
                            ))}
                            {!rows.length && (
                                <p className="px-2 py-1 text-xs text-gray-400">No buyers in this group.</p>
                            )}
                            {bucket.count > 5 && (
                                <p className="px-2 pt-1 text-[11px] text-gray-400">
                                    and {bucket.count - 5} more
                                </p>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/** Where the engagement came from, and what each channel actually produced. */
export function SourceAttribution({ sources = [] }) {
    if (!sources.length) {
        return (
            <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                No attributed traffic recorded yet.
            </p>
        );
    }
    const maxViews = Math.max(...sources.map((s) => s.views), 1);

    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
                <thead>
                    <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500">
                        <th className="py-2 pr-2 font-semibold">Source</th>
                        <th className="px-2 py-2 text-right font-semibold">Views</th>
                        <th className="px-2 py-2 text-right font-semibold">Buyers</th>
                        <th className="px-2 py-2 text-right font-semibold">Enquiries</th>
                        <th className="px-2 py-2 text-right font-semibold">Offers</th>
                        <th className="px-2 py-2 text-right font-semibold">Sold</th>
                        <th className="w-1/4 px-2 py-2 font-semibold">Share</th>
                    </tr>
                </thead>
                <tbody>
                    {sources.map((source) => (
                        <tr key={source.key} className="border-b border-gray-100">
                            <td className="py-2 pr-2">
                                <span className="flex items-center gap-1.5 font-semibold text-[#374151]">
                                    <Radio size={12} className="text-[#55C5D0]" /> {source.label}
                                </span>
                            </td>
                            <td className="px-2 py-2 text-right">{source.views.toLocaleString()}</td>
                            <td className="px-2 py-2 text-right">{source.buyers}</td>
                            <td className="px-2 py-2 text-right">{source.enquiries}</td>
                            <td className="px-2 py-2 text-right">{source.offers}</td>
                            <td className="px-2 py-2 text-right">{source.sold}</td>
                            <td className="px-2 py-2">
                                <span className="block h-2 w-full rounded-full bg-gray-100">
                                    <span
                                        className="block h-2 rounded-full bg-[#55C5D0]"
                                        style={{ width: `${(source.views / maxViews) * 100}%` }}
                                    />
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Per open home: who registered, who turned up, and what happened next. */
export function OpenHomeAnalytics({ openHomes = [] }) {
    if (!openHomes.length) {
        return (
            <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                No open homes recorded for this property yet.
            </p>
        );
    }

    return (
        <div className="space-y-2">
            {openHomes.map((inspection) => (
                <div key={inspection.id} className="rounded-xl border border-gray-200 bg-white p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="flex items-center gap-1.5 text-sm font-bold text-[#374151]">
                            <CalendarDays size={14} className="text-[#55C5D0]" />
                            {inspection.scheduled_date
                                ? new Date(inspection.scheduled_date).toLocaleString('en-AU', {
                                    timeZone: MELBOURNE_TZ,
                                    weekday: 'short', day: 'numeric', month: 'short',
                                    hour: 'numeric', minute: '2-digit',
                                })
                                : 'Scheduled'}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                            {inspection.status}
                        </span>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6">
                        {[
                            ['Registered', inspection.registrations],
                            ['Attended', inspection.attendees],
                            ['No shows', inspection.no_shows],
                            ['Repeat attendees', inspection.repeat_attendees],
                            ['Enquiries after', inspection.enquiries_after],
                            ['Offers after', inspection.offers_after],
                        ].map(([label, value]) => (
                            <div key={label} className="rounded-lg bg-[#f8fafb] p-2 text-center">
                                <div className="text-lg font-extrabold text-[#374151]">{value}</div>
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{label}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                        <span className="rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-600">
                            Hot {inspection.interest_levels?.hot || 0}
                        </span>
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
                            Warm {inspection.interest_levels?.warm || 0}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-600">
                            Cold {inspection.interest_levels?.cold || 0}
                        </span>
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                            Still active {inspection.still_active}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-500">
                            Gone cold {inspection.gone_cold}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
}

/** This campaign against comparable Collings campaigns at the same age. */
export function BenchmarkPanel({ benchmark }) {
    if (!benchmark?.available) {
        return (
            <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                Not enough comparable campaigns yet to benchmark this property.
            </p>
        );
    }

    return (
        <div>
            <p className="mb-3 text-xs text-gray-500">
                Compared with {benchmark.sample_size} comparable {benchmark.basis.toLowerCase()} over their first{' '}
                {benchmark.compared_over_days} days.
            </p>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                {benchmark.metrics.map((metric) => {
                    const tone = metric.direction === 'up'
                        ? 'text-emerald-600'
                        : metric.direction === 'down' ? 'text-red-500' : 'text-gray-400';
                    return (
                        <div key={metric.key} className="rounded-xl border border-gray-200 bg-white p-3">
                            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                                <BarChart3 size={12} className="text-[#55C5D0]" /> {metric.label}
                            </div>
                            <div className="mt-1 text-2xl font-extrabold text-[#374151]">
                                {metric.value.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-gray-500">typically {metric.benchmark}</div>
                            <div className={`mt-1 text-xs font-bold ${tone}`}>
                                {metric.display}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
